import type { JSX } from 'react'
import { type MouseEvent, useState } from 'react'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { copyToClipboard } from '../../theme/util/clipboard-util'
import type { Styles } from '../types/Styles'

export { PropertyRow }

type TextProps = {
  value?: string | number | null
  variant?: 'h5' | 'body1' | 'body2'
}

type ReactNodeProps = {
  value?: JSX.Element[] | JSX.Element
  variant?: never
}

type Props = {
  label: string
  copy?: string
  sx?: Styles
} & (TextProps | ReactNodeProps)

const PropertyRow = ({
  label,
  copy,
  value: valueFromProps,
  variant = 'body1',
  sx,
}: Props) => {
  let value = valueFromProps

  if (
    (typeof value === 'string' && !value.trim()) ||
    value === undefined ||
    value === null
  ) {
    value = '--'
  }

  const title = `${label} kopiëren`

  const [tooltipTitle, setTooltipTitle] = useState(title)

  const handleCopy = async (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()

    if (copy) {
      const copied = await copyToClipboard(copy)

      if (copied) {
        setTooltipTitle(`${label} gekopieerd`)
      }
    }
  }

  const resetCopyTooltip = () => {
    setTooltipTitle(title)
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', ...sx }}>
      <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
        {label}
      </Typography>

      {typeof value === 'object' ? (
        <>
          {value}

          {copy && (
            <Tooltip title={tooltipTitle} onOpen={resetCopyTooltip}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{ padding: 0, marginLeft: 1, verticalAlign: 'text-bottom' }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ) : (
        <Typography variant={variant}>
          {value}

          {copy && (
            <Tooltip title={tooltipTitle} onOpen={resetCopyTooltip}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{ padding: 0, marginLeft: 1 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      )}
    </Box>
  )
}
